// hooks/useSessionTabs.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRegistrationService, RegistrationSessionModelView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { buildSessionTabs, SessionTabEntry } from '../Tabs/buildSessionTabs';

export interface UseSessionTabsResult {
  tabs: SessionTabEntry[];
  loading: boolean;
  loaded: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetches a registration's sessions and derives the dynamic tab list so the calling page can render
 * them as top-level tabs.
 *
 * Campus version of the admin hook: the course session catalogue is no longer fetched. It only fed the
 * "Add Session" menu and an `orderNo` fallback, and `RegistrationSessionModelView.orderNo` now carries
 * the order itself.
 */
export function useSessionTabs(registrationId: number, active: boolean): UseSessionTabsResult {
  const registrationService = useRegistrationService();

  const [sessions, setSessions] = useState<RegistrationSessionModelView[] | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchSessions = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    try {
      const res: any = await registrationService.getSessions(registrationId);
      const data: RegistrationSessionModelView[] = res?.data ?? res?.items ?? res ?? [];
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error fetching registration sessions:', e);
      toast.error('Error', 'Could not load registration sessions.');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [registrationId, registrationService]);

  useEffect(() => {
    if (active && sessions === null) fetchSessions();
  }, [active, sessions, fetchSessions]);

  const tabs = useMemo(() => (sessions ? buildSessionTabs(sessions) : []), [sessions]);

  return { tabs, loading, loaded: sessions !== null, refetch: fetchSessions };
}
